/**
 * Call screen (web) -- join a video-call room.
 *
 * Zero-signup: pressing Join resolves a CallSession (a real atproto identity if
 * logged in, else a client-generated guest did:key), mints a service-auth token
 * for the calls worker, and mounts <Conference>. Native gets a stub (index.tsx).
 */

import {useState} from 'react'
import {View} from 'react-native'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'

import {type CommonNavigatorParams} from '#/lib/routes/types'
import {useAgent, useSession} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Layout from '#/components/Layout'
import {Text} from '#/components/Typography'
import {makeCallFetch} from './cfcalls/callFetch'
import {type CallSession, resolveCallSession} from './cfcalls/callToken'
import {Conference} from './cfcalls/components/Conference.web'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'Call'>

export function Call({route}: Props) {
  const t = useTheme()
  const {roomId} = route.params
  const {hasSession, currentAccount} = useSession()
  const agent = useAgent()

  const [joined, setJoined] = useState(false)
  const [joining, setJoining] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [session, setSession] = useState<CallSession | null>(null)
  const [token, setToken] = useState<string | null>(null)

  // Stable fetch bound to the resolved session's token source (React Compiler
  // memoizes this; no manual useMemo).
  const callFetch = session ? makeCallFetch(session.getToken) : null

  const onJoin = () => {
    setJoining(true)
    setError(null)
    resolveCallSession({
      agent,
      hasSession,
      handle: currentAccount?.handle,
    })
      .then(async s => {
        const tok = await s.getToken()
        setSession(s)
        setToken(tok)
        setJoined(true)
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Could not join the call')
      })
      .finally(() => {
        setJoining(false)
      })
  }

  const onLeave = () => {
    setJoined(false)
    setSession(null)
    setToken(null)
  }

  if (joined && session && callFetch) {
    return (
      <Layout.Screen testID="callScreen">
        <Conference
          roomSlug={roomId}
          session={session}
          callFetch={callFetch}
          token={token}
          onLeave={onLeave}
        />
      </Layout.Screen>
    )
  }

  return (
    <Layout.Screen testID="callScreen">
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>Video call</Layout.Header.TitleText>
        </Layout.Header.Content>
        <Layout.Header.Slot />
      </Layout.Header.Outer>
      <View
        style={[a.flex_1, a.align_center, a.justify_center, a.gap_lg, a.p_xl]}>
        <Text style={[a.text_xl, a.font_bold, t.atoms.text]}>
          Room: {roomId}
        </Text>
        <Text style={[a.text_md, t.atoms.text_contrast_medium, a.text_center]}>
          {hasSession
            ? 'Join with your account. Your camera and mic turn on when you join.'
            : 'No account needed — join as a guest. Your camera and mic turn on when you join.'}
        </Text>
        {error && (
          <Text style={[a.text_sm, {color: t.palette.negative_500}]}>
            {error}
          </Text>
        )}
        <Button
          label="Join call"
          onPress={onJoin}
          disabled={joining}
          color="primary"
          size="large">
          <ButtonText>{joining ? 'Joining…' : 'Join call'}</ButtonText>
        </Button>
      </View>
    </Layout.Screen>
  )
}
